import { useCallback, useMemo } from 'react';
import { isArray, isNumber, keyBy, sortBy } from 'lodash';

import { ActivitySortingType, AppliedActivityListFilters } from '@/controllers/Activity';
import { createDefaultActivityFilters, TabType } from '@/pages/Dashboard/ActivityList/helpers';
import { ActivityStatus } from '@/types';

import { useSearchParams } from '../useSearchParams/useSearchParams';

const parseNumber = <T extends number = number>(
  fallback: T,
  enumType: Record<number | string, T | string>,
  value: string | null,
) => {
  if (value == null) {
    return fallback;
  }

  const $value = Number(value);

  if (Number.isNaN($value)) {
    return fallback;
  }

  const candidates = keyBy(Object.values(enumType));

  return Object.prototype.hasOwnProperty.call(candidates, $value) ? $value : fallback;
};

const parseBoolean = (fallback: boolean, value: string | null) => {
  if (value == null) {
    return fallback;
  }

  const $value = value.toLowerCase();

  if ($value === 'false') {
    // This is a special case in which we want to return false, regardless of
    // the fallback value.
    return false;
  }

  return $value === 'true' || fallback;
};

const parseString = (fallback: string, value: string | null) => value || fallback;

const parseStatuses = (statuses: string | null): ActivityStatus[] => {
  const { statuses: DEFAULT_ACTIVITY_FILTER_STATUS } = createDefaultActivityFilters();

  if (!statuses) {
    return DEFAULT_ACTIVITY_FILTER_STATUS;
  }

  try {
    const result = JSON.parse(statuses);

    if (!isArray(result)) {
      return DEFAULT_ACTIVITY_FILTER_STATUS;
    }

    const valid = keyBy(Object.values(ActivityStatus));

    return result
      .filter(isNumber)
      .filter(candidate => Object.prototype.hasOwnProperty.call(valid, candidate));
  } catch {
    return DEFAULT_ACTIVITY_FILTER_STATUS;
  }
};

function fromUrlSearchParams(
  params: URLSearchParams,
  initial = createDefaultActivityFilters(),
): AppliedActivityListFilters {
  return {
    ...initial,
    type: parseNumber(initial.type, TabType, params.get('type')),
    searchQuery: parseString(initial.searchQuery, params.get('searchQuery')),
    sortingType: parseNumber(initial.sortingType, ActivitySortingType, params.get('sortingType')),
    starredOnly: parseBoolean(initial.starredOnly, params.get('starredOnly')),
    statuses: parseStatuses(params.get('statuses')),
  };
}

function toUrlSearchParams(filters: AppliedActivityListFilters = {}) {
  const initial = createDefaultActivityFilters();

  const {
    type = initial.type,
    statuses = initial.statuses,
    sortingType = initial.sortingType,
    starredOnly = initial.starredOnly,
    searchQuery = initial.searchQuery,
  } = filters;

  const pairs: Record<string, string> = {};

  if (type !== initial.type) {
    pairs.type = JSON.stringify(type);
  }

  if (JSON.stringify(sortBy(statuses)) !== JSON.stringify(sortBy(initial.statuses))) {
    pairs.statuses = JSON.stringify(statuses);
  }

  if (sortingType !== initial.sortingType) {
    pairs.sortingType = JSON.stringify(sortingType);
  }

  if (starredOnly !== initial.starredOnly) {
    pairs.starredOnly = JSON.stringify(starredOnly);
  }

  if (searchQuery !== initial.searchQuery) {
    pairs.searchQuery = searchQuery;
  }

  return new URLSearchParams(pairs);
}

export const useActivityFilters = (): [
  AppliedActivityListFilters,
  (next: AppliedActivityListFilters | undefined) => void,
] => {
  const [params, setParams] = useSearchParams();

  const filters = useMemo(() => fromUrlSearchParams(params), [params]);

  const setFilters = useCallback(
    (filters: AppliedActivityListFilters | undefined) => setParams(toUrlSearchParams(filters)),
    [setParams],
  );

  return [filters, setFilters];
};
